// UserModal — modal-embedded form for creating or updating a User row.
// Refactored 2026-05-05 (Phase 4, Class P) — hand-rolled Modal+Box → MUI Dialog
// with ModalFormHeader, Grid layout, and properly-scoped FormControls.

import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { PersonAddAlt1Outlined, ManageAccountsOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { createUser, updateUser } from "../../feature/userSlice";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import { validateInput } from "../../utils/validateInput";
import { ModalFormHeader, FieldLabel } from "../PaperForm";
import { FIELD_SX, SectionHeader } from "../WeldAssure/BrandedForm";

const UserModal = (props) => {
  const { user } = props;
  const dispatch = useDispatch();
  const isEdit = Boolean(user);

  const [data, setData] = useState({
    firstName: user ? user.firstName : "",
    lastName: user ? user.lastName : "",
    email: user ? user.email : "",
    password: user ? user.password : "",
    ratePerHour: user ? user.ratePerHour : "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedRole = ""] = user?.roles || [];
  const [role, setRole] = useState(user ? selectedRole?.id : "");
  const [phone, setPhone] = useState(user ? user.phone : "");
  const [isActive, setIsActive] = useState(user ? user.isActive : false);

  const { roles } = useSelector((state) => state.roleSlice);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const formatPhoneNumber = (input) => {
    const numbersOnly = input.replace(/\D/g, "");
    let formattedValue = "";
    if (numbersOnly.length > 0) formattedValue += numbersOnly.slice(0, 3);
    if (numbersOnly.length >= 4) formattedValue += `-${numbersOnly.slice(3, 6)}`;
    if (numbersOnly.length >= 7) formattedValue += `-${numbersOnly.slice(6, 11)}`;
    return formattedValue;
  };

  const handlePhoneChange = (event) => {
    setPhone(formatPhoneNumber(event.target.value));
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleSubmit = () => {
    if (!data.ratePerHour) {
      showErrorToast("Please enter a Rate Per Hour value.");
      return;
    }
    if (!phone) {
      showErrorToast("Please Add User Phone Number");
      return;
    }
    if (!validateInput("role", role)) return;

    if (!user) {
      for (const field of ["firstName", "lastName", "email", "password"]) {
        if (!validateInput(field, data[field])) return;
      }
      if (data.password !== confirmPassword) {
        showErrorToast("Passwords do not match");
        return;
      }
    } else {
      for (const field of ["firstName", "lastName", "email"]) {
        if (!validateInput(field, data[field])) return;
      }
    }

    if (phone.length > 0 && !validateInput("phone", phone)) return;

    const userData = {
      ...data,
      id: user?.id,
      phone,
      roleId: role,
      isActive,
    };

    if (user) {
      dispatch(updateUser(userData)).then((res) => {
        if (res.type === "update-users/users/rejected") {
          showErrorToast(res.payload);
        }
        if (res && res.payload?.id) {
          showSuccessToast("User Updated Successfully !");
          handleClose();
        }
      });
    } else {
      dispatch(createUser(userData)).then((res) => {
        if (res.type === "create-users/users/rejected") {
          showErrorToast("User Already Exist");
        }
        if (res.payload?.id) {
          showSuccessToast("User Created Successfully !");
          handleClose();
        }
      });
    }
  };

  const buttonLabel = props.isLoading
    ? "Loading…"
    : isEdit
    ? "Save changes"
    : "Create User";

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <ModalFormHeader
          overline="SFM · Users & Roles"
          title={isEdit ? "Edit User" : "Add User"}
          icon={isEdit ? ManageAccountsOutlined : PersonAddAlt1Outlined}
        />

        <Stack spacing={2.5}>
          <Box>
            <FieldLabel required>Role</FieldLabel>
            <FormControl fullWidth size="small" sx={FIELD_SX}>
              <Select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                displayEmpty
                fullWidth
                variant="outlined"
                sx={{ textAlign: "left" }}
                renderValue={(v) => {
                  const r = roles.find((x) => x.id === v);
                  return r ? r.name : <span style={{ color: "#9aa3ad" }}>Select a role</span>;
                }}
              >
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <SectionHeader>Personal details</SectionHeader>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" sx={FIELD_SX}
                defaultValue={data.firstName}
                onChange={handleInputChange}
                name="firstName"
                label="First Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" sx={FIELD_SX}
                defaultValue={data.lastName}
                onChange={handleInputChange}
                name="lastName"
                label="Last Name"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" sx={FIELD_SX}
                defaultValue={data.email}
                onChange={handleInputChange}
                name="email"
                label="Email"
              />
            </Grid>

            {!isEdit && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small" sx={FIELD_SX}
                    onChange={handleInputChange}
                    name="password"
                    label="Password"
                    type="password"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small" sx={FIELD_SX}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    error={confirmPassword.length > 0 && data.password !== confirmPassword}
                    helperText={
                      confirmPassword.length > 0 && data.password !== confirmPassword
                        ? "Passwords do not match"
                        : ""
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" sx={FIELD_SX}
                value={phone}
                name="phone"
                label="Phone"
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneChange}
                placeholder="XXX-XXX-XXXX"
                inputMode="numeric"
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" sx={FIELD_SX}
                defaultValue={data.ratePerHour}
                onChange={handleInputChange}
                name="ratePerHour"
                label="Rate Per Hour"
              />
            </Grid>
          </Grid>

          {isEdit && (
            <Box
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}>
                ACCOUNT ACTIVE
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={() => setIsActive((prev) => !prev)}
                    color="primary"
                  />
                }
                label={isActive ? "Active" : "Disabled"}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
            flex: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={props.isLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
            flex: 2,
          }}
        >
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
